import { Link, useLocation } from 'react-router-dom';
import type { ToolConfig } from '@/shared/config/tools';
import landingPages from '@/shared/config/landing-pages.json';

export type LandingDetails = {
  path?: string;
  intent: string;
  exampleInput: string;
  exampleOutput: string;
  defaultProps?: Record<string, unknown>;
  guide?: {
    workflow: string[];
    useCases: string[];
    notes: string[];
    links: {
      path: string;
      label: string;
      description: string;
    }[];
  };
  faq?: {
    question: string;
    answer: string;
  }[];
};

type ToolGuideProps = {
  title: string;
  tool: ToolConfig;
  relatedTools: ToolConfig[];
  landing?: LandingDetails;
};

const NETWORK_SECURITY_ANSWER =
  'This tool sends requests to the destination you choose, which may receive your IP address and request metadata. Review the tool’s notes and do not submit secrets you do not intend to send.';
const BROWSER_SECURITY_ANSWER =
  'Many operations run locally in your browser, but storage and network behavior vary by tool. Review this page’s notes before entering sensitive data, and clear site data to remove values saved in this browser.';

// Search tasks include Turkish aliases for on-site search; only English tasks are shown as copy.
// Keep in sync with renderTasks() in scripts/prerender-seo.js.
const NON_ENGLISH_TASK =
  /(\b(et|olc|olustur|cevir|hesapla|yap|say|kontrol|iki|tarih|arasinda|kac|gun|saat|adres\w*|ogren\w*)\b|[çğıöşü])/i;

const CATEGORY_OVERVIEW_HEADINGS: Record<string, string> = {
  converters: 'What this conversion does',
  encoders: 'How this encoding and decoding works',
  generators: 'How this generator works',
  security: 'How this security utility works',
  network: 'What this network diagnostic measures',
  hardware: 'What this hardware diagnostic tests',
  image: 'How this media processing works',
  time: 'How this date and time calculation works',
  color: 'How this color and design utility works',
  ai: 'How this AI prompt and token utility works',
  data: 'How this data and SQL utility works',
  text: 'How this text processing works',
  development: 'How this developer utility works',
};

export function overviewHeading(category: string): string {
  return CATEGORY_OVERVIEW_HEADINGS[category] ?? 'How this utility works';
}

export function displayTasks(tasks: readonly string[] | undefined): string[] {
  return (tasks ?? [])
    .filter((task) => !NON_ENGLISH_TASK.test(task))
    .map((task) => task.charAt(0).toUpperCase() + task.slice(1));
}

export function ToolGuide({ title, tool, relatedTools, landing }: ToolGuideProps) {
  const { pathname } = useLocation();
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const search = tool.search ?? { actions: [] };
  const inputs = search.inputs?.slice(0, 3).join(', ');
  const outputs = search.outputs?.slice(0, 3).join(', ');
  const primaryAction = search.actions[0] ?? 'process';
  const tasks = displayTasks(search.tasks);
  const hasCustomWorkflow = Boolean(landing?.guide?.workflow?.length);
  const toolLandings = landingPages.filter(
    (page) => page.toolId === tool.id && page.path !== normalizedPath,
  );

  return (
    <article className="space-y-8 border-t pt-8" aria-labelledby="tool-guide-title">
      {!hasCustomWorkflow && (
        <section className="space-y-3">
          <h2 id={!landing ? 'tool-guide-title' : undefined} className="text-xl font-semibold">
            How to use this tool
          </h2>
          <ol className="list-decimal space-y-2 pl-5 text-muted-foreground">
            <li>
              {inputs
                ? `Enter or select the ${inputs} value you want to work with.`
                : 'Choose the options that match the result you need.'}
            </li>
            <li>Review the available settings, then run the {primaryAction} action.</li>
            <li>
              Review {outputs ? `the ${outputs} output` : 'the result'} and use the copy or download
              action when the tool provides one.
            </li>
          </ol>
        </section>
      )}

      {tasks.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold">What you can do with {tool.name}</h2>
          <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
            {tasks.map((task) => (
              <li key={task}>{task}</li>
            ))}
          </ul>
        </section>
      )}

      {landing && (
        <>
          <section className="space-y-4">
            <div>
              <h2 id="tool-guide-title" className="text-xl font-semibold">
                {overviewHeading(tool.category)}
              </h2>
              <p className="mt-2 leading-7 text-muted-foreground">{landing.intent}</p>
            </div>
            <div className="grid min-w-0 gap-4 md:grid-cols-2">
              <div className="min-w-0 rounded-lg border bg-muted/30 p-4">
                <h3 className="font-medium">Example input</h3>
                <pre className="mt-3 min-w-0 overflow-x-auto whitespace-pre-wrap text-sm [overflow-wrap:anywhere]">
                  <code>{landing.exampleInput}</code>
                </pre>
              </div>
              <div className="min-w-0 rounded-lg border bg-muted/30 p-4">
                <h3 className="font-medium">Example output</h3>
                <pre className="mt-3 min-w-0 overflow-x-auto whitespace-pre-wrap text-sm [overflow-wrap:anywhere]">
                  <code>{landing.exampleOutput}</code>
                </pre>
              </div>
            </div>
          </section>

          {landing.guide && (
            <>
              <section className="space-y-3">
                <h2 className="text-xl font-semibold">Practical workflow</h2>
                <ol className="list-decimal space-y-2 pl-5 text-muted-foreground">
                  {landing.guide.workflow.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </section>

              <section className="space-y-3">
                <h2 className="text-xl font-semibold">Common use cases</h2>
                <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
                  {landing.guide.useCases.map((useCase) => (
                    <li key={useCase}>{useCase}</li>
                  ))}
                </ul>
              </section>

              <section className="space-y-3">
                <h2 className="text-xl font-semibold">Important notes and limitations</h2>
                <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
                  {landing.guide.notes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </section>

              <section className="space-y-3">
                <h2 className="text-xl font-semibold">Continue your workflow</h2>
                <ul className="space-y-3">
                  {landing.guide.links.map((link) => (
                    <li key={link.path}>
                      <Link
                        className="font-medium text-link underline underline-offset-4"
                        to={link.path}
                      >
                        {link.label}
                      </Link>
                      <span className="text-muted-foreground"> — {link.description}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </>
      )}

      <section className="space-y-4">
        <h2 className="text-xl font-semibold">Frequently asked questions</h2>
        <div className="space-y-3">
          {landing?.faq && landing.faq.length > 0 ? (
            landing.faq.map((item) => (
              <div key={item.question} className="rounded-lg border bg-card p-4">
                <h3 className="font-medium text-foreground">{item.question}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.answer}</p>
              </div>
            ))
          ) : (
            <>
              <div className="rounded-lg border bg-card p-4">
                <h3 className="font-medium text-foreground">Is {title} free?</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Yes. EasyUtils tools are completely free to use without an account or signup.
                </p>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <h3 className="font-medium text-foreground">Can I use it on mobile?</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Yes. The interface is fully responsive and works smoothly in all modern desktop
                  and mobile browsers.
                </p>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <h3 className="font-medium text-foreground">Is my data secure?</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {tool.category === 'network' ? NETWORK_SECURITY_ANSWER : BROWSER_SECURITY_ANSWER}
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {toolLandings.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold">Focused guides for this tool</h2>
          <ul className="space-y-3">
            {toolLandings.map((page) => (
              <li key={page.path}>
                <Link className="font-medium text-link underline underline-offset-4" to={page.path}>
                  {page.title}
                </Link>
                <span className="text-muted-foreground"> — {page.intent}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {relatedTools.length > 0 && !landing?.guide?.links?.length && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold">Related tools</h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {relatedTools.map((related) => (
              <li key={related.id}>
                <Link
                  to={related.path}
                  className="block rounded-lg border p-3 font-medium transition-colors hover:bg-accent"
                >
                  {related.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
